import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useToastContext } from '../App';

function UserManagement() {
  const [users, setUsers] = useState({ admins: [], players: [] });
  const [guests, setGuests] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [showCreate, setShowCreate] = useState(false);
  const [showResetPw, setShowResetPw] = useState(null);
  const [loading, setLoading] = useState(true);
  const addToast = useToastContext();

  const [form, setForm] = useState({ username: '', password: '', role: 'player' });
  const [resetPwForm, setResetPwForm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const usersData = await api.getUsers();
      setUsers(usersData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function loadGuests() {
    try {
      const data = await api.getGuestLog();
      setGuests(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    try {
      await api.createUser(form);
      addToast(`Benutzer "${form.username}" erstellt`, 'success');
      setShowCreate(false);
      setForm({ username: '', password: '', role: 'player' });
      const data = await api.getUsers();
      setUsers(data);
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleDelete(id, username) {
    if (!window.confirm(`Benutzer "${username}" wirklich loeschen?`)) return;
    try {
      await api.deleteUser(id);
      addToast(`Benutzer "${username}" geloescht`, 'success');
      const data = await api.getUsers();
      setUsers(data);
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    if (!resetPwForm || resetPwForm.length < 4) {
      addToast('Passwort muss mindestens 4 Zeichen haben', 'error');
      return;
    }
    try {
      await api.resetPassword(showResetPw.id, resetPwForm);
      addToast(`Passwort fuer "${showResetPw.username}" zurueckgesetzt`, 'success');
      setShowResetPw(null);
      setResetPwForm('');
      loadData();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleClearGuests() {
    if (!window.confirm('Gast-Protokoll wirklich loeschen?')) return;
    try {
      await api.clearGuestLog();
      addToast('Gast-Protokoll geloescht', 'success');
      setGuests([]);
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  if (loading) {
    return <div className="empty-state"><div className="spinner" /></div>;
  }

  const allUsers = [
    ...users.admins.map(u => ({ ...u, role: 'admin' })),
    ...users.players.map(u => ({ ...u, role: 'player' })),
  ];

  return (
    <div>
      <div className="page-header">
        <h2>Benutzerverwaltung</h2>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + Neuer Benutzer
        </button>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
          Benutzer ({allUsers.length})
        </button>
        <button className={`tab ${activeTab === 'guests' ? 'active' : ''}`} onClick={() => { setActiveTab('guests'); loadGuests(); }}>
          Gast-Protokoll
        </button>
      </div>

      {activeTab === 'users' && (
        <div className="table-container">
          {allUsers.length === 0 ? (
            <div className="empty-state">
              <div className="icon">&#128100;</div>
              <p>Keine Benutzer vorhanden</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Benutzername</th>
                  <th>Rolle</th>
                  <th>Erstellt am</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {allUsers.map(u => (
                  <tr key={u.id}>
                    <td>
                      <span className="fw-bold">{u.username}</span>
                      {u.mustChangePassword && (
                        <span className="tag" style={{ marginLeft: 8, background: 'rgba(243,156,18,0.15)', color: 'var(--warning)' }}>
                          PW-Aenderung
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`tag ${u.role === 'admin' ? 'enabled' : ''}`}>
                        {u.role === 'admin' ? 'Admin' : 'Spieler'}
                      </span>
                    </td>
                    <td className="text-sm text-muted">
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString('de-DE') : '-'}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => { setShowResetPw(u); setResetPwForm(''); }}
                        >
                          Passwort
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(u.id, u.username)}
                        >
                          Loeschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'guests' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <button className="btn btn-danger btn-sm" onClick={handleClearGuests} disabled={guests.length === 0}>
              Protokoll loeschen
            </button>
          </div>
          {guests.length === 0 ? (
            <div className="empty-state">
              <div className="icon">&#128065;</div>
              <p>Noch keine Gast-Besuche protokolliert</p>
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>IP-Adresse</th>
                    <th>Zeitpunkt</th>
                  </tr>
                </thead>
                <tbody>
                  {guests.map(g => (
                    <tr key={g.id}>
                      <td className="fw-bold">{g.name}</td>
                      <td className="text-sm text-muted">{g.ip}</td>
                      <td className="text-sm text-muted">
                        {g.loginAt ? new Date(g.loginAt).toLocaleString('de-DE') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create User Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Neuen Benutzer erstellen</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Benutzername</label>
                <input
                  className="form-input"
                  value={form.username}
                  onChange={e => setForm({ ...form, username: e.target.value })}
                  placeholder="Benutzername"
                  minLength={2}
                  maxLength={32}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Passwort</label>
                <input
                  className="form-input"
                  type="password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="Mindestens 4 Zeichen"
                  minLength={4}
                  required
                />
              </div>
              <div className="form-group">
                <label>Rolle</label>
                <select
                  className="form-select"
                  value={form.role}
                  onChange={e => setForm({ ...form, role: e.target.value })}
                >
                  <option value="player">Spieler</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-primary">Erstellen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPw && (
        <div className="modal-overlay" onClick={() => setShowResetPw(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Passwort zuruecksetzen</h3>
            <p className="text-muted" style={{ marginBottom: 16 }}>
              Neues Passwort fuer <strong>{showResetPw.username}</strong>
            </p>
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label>Neues Passwort</label>
                <input
                  className="form-input"
                  type="password"
                  value={resetPwForm}
                  onChange={e => setResetPwForm(e.target.value)}
                  placeholder="Mindestens 4 Zeichen"
                  minLength={4}
                  required
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowResetPw(null)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-primary">Zuruecksetzen</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
